import { useState } from 'react'
import { useToast } from '@/hooks/useToast'
import { Button } from './Button'

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value)
    return
  }
  // Safari denies clipboard access on insecure origins and older iOS; the
  // textarea trick is the only thing that works there.
  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  const ok = document.execCommand('copy')
  document.body.removeChild(textarea)
  if (!ok) throw new Error('Copy was blocked by the browser')
}

export function CopyButton({
  value,
  label = 'Copy',
  size = 'sm',
  variant = 'secondary',
}: {
  value: string
  label?: string
  size?: 'sm' | 'md'
  variant?: 'primary' | 'secondary' | 'ghost'
}) {
  const [copied, setCopied] = useState(false)
  const toast = useToast()

  return (
    <Button
      size={size}
      variant={variant}
      onClick={async () => {
        try {
          await copyText(value)
          setCopied(true)
          window.setTimeout(() => setCopied(false), 2000)
        } catch (error) {
          toast.fromError(error, 'Could not copy')
        }
      }}
    >
      {/*
        Only the confirmation mark is drawn. A clipboard glyph beside the word
        "Copy" says nothing the word does not, and the design's one rule about
        ornament is that it has to carry information.
      */}
      {copied ? (
        <svg viewBox="0 0 20 20" className="size-4" fill="none" aria-hidden="true">
          <path
            d="M4.5 10.5l3.5 3.5 7.5-8"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
      <span aria-live="polite">{copied ? 'Copied' : label}</span>
    </Button>
  )
}
